<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="playlists"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">
          
          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>
          
          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog v-model="editDialog" max-width="400px" :persistent="true">
              <loading :active.sync="isSaving" :can-cancel="false" loader="dots" :color="appColor_Progress"></loading>
              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice" small dark slot="activator" :color="toolbarColorDark" class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">New</v-btn>
                <v-btn v-else slot="activator" :color="toolbarColorDark" large dark class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">{{ getExpression('Button', 'CreateAlbum') }}</v-btn>
              </template>
              <v-toolbar dense dark flat :color="toolbarColorDark">
                <v-toolbar-title><v-icon class="pr-2">edit</v-icon> {{ formTitle }}</v-toolbar-title>
              </v-toolbar>
              <v-card>
                <v-card-text class="pt-3 pl-2 pr-2 pb-0 ma-0">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedPlaylist.name"
                            :label="getExpression('Label', 'Name')"
                            v-validate="'required|max:50'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            maxlength="50"
                            ref="name"
                            >
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedPlaylist.description"
                            :label="getExpression('Label', 'Description')"
                            maxlength="100"
                            >
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12">
                          <v-checkbox
                            v-model="editedPlaylist.isSlideShow"
                            :label="getExpression('Label', 'SlideShow')"
                            :color="appColor_Selected"
                            hide-details
                            >
                          </v-checkbox>
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions>

                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>

                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
        
          <!-- Kiosks Dropdown -->
          <v-col v-if="showKiosk" cols="12" sm="4">
            <kiosk-combobox
              v-model="kioskId"
              :options="kiosks"
              :label="getExpression('Label', 'Kiosk')"
              :placeholder="getExpression('Label', 'SelectKiosk')"
              :disabled="disableKiosk"
              class="mr-3"
            />
          </v-col>

          <!-- Residents Dropdown -->
          <v-col v-if="showResident" cols="12" sm="4">
            <v-select
              v-model="residentId"
              :items="filteredResidents"
              item-value="id"
              item-text="name"
              :label="getExpression('Label', 'Resident')"
              :placeholder="getExpression('Label', 'SelectResident')"
              @change="loadPlaylists()"
              :disabled="disableResident"
              class="mr-3"
            />
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" :sm="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="disableSearch"
              clearable
              >
            </v-text-field>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.coverImageBlobName="{ item }">
        <span>
          <router-link :to="currentRoute" style="text-decoration: none">
            <span @click="pickCoverImage(item)">
              <v-badge
                bordered
                overlap
                right
                icon="edit"
                color="grey"
                class="mt-2"
                >
                <span v-if="item.coverImageBlobName !== null && !isLoading">
                  <v-img width="50" height="50" :src="getCoverImageUri(item)" style="float: left"></v-img>
                </span>
                <span v-else style="white-space: nowrap">
                  <v-icon size="48" class="ma-0">image</v-icon>
                </span>
              </v-badge>
            </span>
          </router-link>
        </span>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.blobs="{ item }">
        <v-btn small dark @click="editDetail(item)" :color="toolbarColorDark" height="36" class="rounded-0">{{ getExpression('Button', 'EditContent') }}</v-btn>
      </template>

      <template v-slot:item.isSlideShow="{ item }">
        <span class="text-center">
          <v-icon v-if="item.isSlideShow" :color="appColor_Selected">done</v-icon>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'" /></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Playlist Detail -->
    <v-dialog v-model="detailDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <playlist-detail
        v-model="editedPlaylist.playlistBlobs"
        v-bind:id="currentPlaylistId"
        v-bind:name="editedPlaylist.name"
        v-bind:ownerName="residentName"
        v-bind:defaultContainer="defaultContainer"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-bind:coverImageUri="getCoverImageUri(editedPlaylist)"
        v-bind:isFromDashboard="isFromDashboard"
        v-on:cancel="detailCancel"
        v-on:done="detailDone"
      />
    </v-dialog>

    <!-- Cover Image Picker -->
    <v-dialog v-model="coverImageDialog" :persistent="true" width="900px">
      <media-selector
        v-model="mediaSelectorParams"
        :title="getExpression('FormTitle', 'SelectCoverImage')"
        :containers="coverImageContainers"
        :defaultContainer="residentContainer"
        :containerLabel="getExpression('Label', 'Library')"
        :toolbarColor="toolbarColor"
        :toolbarColorDark="toolbarColorDark"
        v-on:cancel="coverImageDialog = false"
        v-on:ok="updateCoverImage"
        style="overflow-y: none;"
      >
      </media-selector>
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import PlaylistDetail from '@/components/PlaylistDetail'
import MediaSelector from '@/components/MediaSelector'
import KioskCombobox from '@/components/KioskCombobox'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import { mapGetters } from 'vuex'
import { Container, AppColor, AppStyle, ThumbnailUrl, KioskTypeId } from '@/config.js'

export default {
  components: {
    PlaylistDetail,
    MediaSelector,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  name: 'ResidentPlaylistManager',
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // kiosk type ids
      kioskTypeId_Genie: KioskTypeId.Genie,

      // containers
      residentPrefix: Container.ResidentPrefix,
      publicContainer: Container.Public,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // url
      thumbnailUrl: ThumbnailUrl.Genie,

      isLoading: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      detailDialog: false,
      coverImageDialog: false,

      residents: [],
      residentId: null,
      kiosks: [],
      kioskId: -1,
      playlists: [],
      
      currentPlaylistId: -1,
      mediaSelectorParams: {
        controlId: null,
        selectionType: ''
      },

      editedIndex: -1,
      editedPlaylist: {
        id: -1,
        residentId: null,
        name: null,
        description: null,
        isSlideShow: null,
        coverImageMediaItemId: 0,
        createDate: null,
        playlistBlobs: []
      },
      defaultPlaylist: {
        id: -1,
        residentId: null,
        name: null,
        description: null,
        isSlideShow: null,
        coverImageMediaItemId: 0,
        createDate: null,
        playlistBlobs: []
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeResidentItem',
      'activeKioskItem',
      'activeResidentKioskItem',
      'isAdmin',
      'isDomainManager',
      'isKioskManager',
      'isResidentManager',
      'currentRoute',
      'routeStack',
      'previousRoute',
      'locale']),
    title () {
      if (this.isFromKioskDashboard && this.isFromResidentDashboard) {
       return `${this.residentKioskName} / ${this.residentName}`
      } else if (this.isFromResidentDashboard) {
        return this.residentName
      } else if (this.isFromKioskDashboard) {
        return this.residentKioskName
      }
      return 'Memory Albums'
    },
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.residents.length === 0) {
        return this.getExpression('Alert', 'NoGenieResidents')
      }

      if (this.playlists.length === 0 && this.residentId !== null) {
        return this.getExpression('Alert', 'NoPlaylists')
      }

      return ''
    },
    formTitle () {
      return this.editedPlaylist.id < 0 
        ? this.getExpression('FormTitle', 'NewAlbum')
        : this.getExpression('FormTitle', 'EditAlbum')
    },
    headers () {
      return [{
          text: this.getExpression('Label', 'Cover'),
          value: 'coverImageBlobName',
          align: 'center',
          width: '5%',
          sortable: false
        }, 
        {
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: '25%'
        },
        {
          text: this.getExpression('Label', 'Description'),
          value: 'description',
          align: 'left',
          width: '40%'
        },
        {
          value: 'blobs',
          align: 'center',
          width: '5%',
          sortable: false
        },
        {
          text: this.getExpression('Label', 'Added'),
          value: 'createDate',
          align: 'left',
          width: '20%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ]
    },
    dictionary (){
      return {
        custom: {
          name: {
            required: () => this.getExpression('Validation','NameRequired'),
            max: this.getExpression('Validation','NameMaxChars').replace('{0}', '50')
          }
        }
      }
    },
    coverImageContainers () {
      return [{ containerName: this.residentContainer, displayName: this.residentName }]
    },
    toolbarColor () {
      return this.appColor_Resident
    },
    toolbarColorDark () {
      return this.appColor_ResidentDark
    },
    isFromResidentDashboard () {
      if (!this.isValidSession) return false
      return this.isResidentManager && this.profile.residentIds.length === 1
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('ResidentDashboard')
    },
    isFromKioskDashboard () {
      if (!this.isValidSession) return false
      return (this.isKioskManager && this.profile.kioskIds.length === 1) || this.isDomainManager
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('KioskDashboard')
    },
    isFromDashboard () {
      return this.isFromKioskDashboard || this.isFromResidentDashboard
    },
    filteredResidents () {
      if (this.isResidentManager) {
        return this.residents
      }

      const self = this
      return self.residents.filter(r => {
        return r.kioskId === self.kioskId
      })
    },
    residentContainer () {
      return this.activeResidentItem.container
    },
    residentName () {
      if (this.isFromResidentDashboard) return this.activeResidentItem.name

      const self = this
      const residents = this.residents.filter(value => {
        return value.id === self.residentId
      })
      return residents.length > 0
        ?  residents[0].name
        : ''
    },
    residentKioskName () {
      if (this.isNullOrUndefined(this.activeResidentKioskItem)) return null
      return this.activeResidentKioskItem.serialNumber
    },
    activeKioskName () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      return this.activeKioskItem.name
    },
    defaultContainer () {
      if (this.residentId !== null) {
        return `${this.residentPrefix}${this.residentId}`
      }
      return this.publicContainer
    },
    disableAdd () {
      return this.residentId === null
    },
    disableKiosk () {
      return this.kiosks.length === 0 || this.isLoading
    },
    showKiosk () {
      return this.isAdmin && !this.isFromDashboard
    },
    disableResident () {
      return this.filteredResidents.length <= 1 || this.isLoading
    },
    showResident () {
      return this.isAdmin && !this.isFromDashboard
    },
    disableSearch () {
      const residentId = this.residentId ?? 0
      return this.isLoading || residentId === 0
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKiosk
        ? this.showResident
          ? '4'
          : '8'
        : this.showResident
          ? '8'
          : '12'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this

      // dashboard mode
      if (self.isFromDashboard) {
        self.residentId = self.activeResidentItem.id

      // admin mode
      } else {
        self.isLoading = true
        appService.getKioskComboboxOptions(self.kioskTypeId_Genie).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.kiosks = data.options
            self.kioskId = null
          }
          self.isLoading = false
        })
      }
    },

    loadPlaylists () {
      const self = this
      self.isLoading = true

      appService.getResidentPlaylists(self.residentId).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlists = data.playlists
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    getKioskResidents () {
      const self = this

      return new Promise((resolve) => {
        appService.getKioskResidents(self.kioskId).then(data => {
          if (data.errorSeverity !== null) {
            self.handleError(data)
          } else {
            resolve(data)
          }
        })
      })
    },

    edit (item) {
      this.editedIndex = this.playlists.indexOf(item)
      this.editedPlaylist = Object.assign({}, item)
      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.playlists.indexOf(item)
      const name = self.playlists[index].name

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteMemoryAlbum'),
        text: `<b>${name}</b>`,
        onConfirm: () => {
          self.isDeleting = true
          
          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteResidentPlaylist(item.id, updateByUserId).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.playlists.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          self.isSaving = true
          if (isEditMode) {
            var editedPlaylist = self.playlists[self.editedIndex]
            Object.assign(editedPlaylist, self.editedPlaylist)
            const playlistModel = self.getModel(editedPlaylist, isEditMode)
            appService.updateResidentPlaylist(editedPlaylist.id, playlistModel).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                self.isSaving = false
              }
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            appService.addResidentPlaylist(self.getModel(self.editedPlaylist, isEditMode)).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedPlaylist.id = data.playlist.id
                self.editedPlaylist.residentId = data.playlist.residentId
                self.editedPlaylist.isSlideShow = data.playlist.isSlideShow
                self.editedPlaylist.createDate = data.playlist.createDate
                self.playlists.unshift(self.editedPlaylist)
                self.playlists[0].playlistBlobs = []
              }
              self.isSaving = false
              self.closeDialog()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (pl, isEditMode) {
      if (isEditMode) {
        return {
          residentId: this.residentId,
          name: pl.name,
          description: pl.description,
          isSlideShow: pl.isSlideShow ?? false,
          updateByUserId: this.profile.id,
          createDate: pl.createDate
        }
      } else {
        return {
          residentId: this.residentId,
          name: pl.name,
          description: pl.description,
          isSlideShow: pl.isSlideShow ?? false,
          updateByUserId: this.profile.id
        }
      }
    },

    getCoverImageUri (item) {
      if (this.isNullOrUndefined(item.coverImageBlobName)) return null
      const ext = item.coverImageBlobName.split('.').pop()
      const thumnailName = item.coverImageBlobName.replace(ext, 'jpg')
      return `${this.thumbnailUrl}/${this.residentContainer}/${thumnailName}`
    },

    updateCoverImage ({ selectedMedia }) {
      const self = this
      
      self.isSaving = true
      if (selectedMedia.length > 0) {
        const mediaItemId = selectedMedia[0].mediaItemId

        appService.updateResidentPlaylistCoverImage(self.currentPlaylistId, mediaItemId).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.playlists[self.editedIndex].coverImageBlobName = selectedMedia[0].blobName
          }
          self.editedIndex = -1
        }).catch(e => { self.handleCriticalError(e) })

        self.isSaving = false
        self.coverImageDialog = false
      }
    },

    pickCoverImage (item) {
      const self = this
      self.editedIndex = this.playlists.indexOf(item)
      
      // this flushes out the previous picker results 
      // if a different album cover is being edited
      if (self.mediaSelectorParams.controlId > 0) {
        self.setCoverSelectorParams(item.coverMediaItemId)
      }

      self.coverImageDialog = true
      setTimeout(() => {
        self.currentPlaylistId = item.id
        self.setCoverSelectorParams(item.coverMediaItemId)
      }, 100)
    },

    setCoverSelectorParams (mediaItemId) {
      this.mediaSelectorParams = {
        controlId: mediaItemId ?? 0,
        selectionType: 'cover-image'
      }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedPlaylist()
      }, 300)
    },

    resetEditedPlaylist () {
      this.editedPlaylist = Object.assign({}, this.defaultPlaylist)
      this.editedIndex = -1
      this.$validator.reset()
    },

    editDetail (item) {
      const self = this
      self.editedIndex = self.playlists.findIndex(p => p.id === item.id)
      self.detailDialog = true
      setTimeout(() => {
        self.editedPlaylist = Object.assign({}, item)
        self.currentPlaylistId = self.editedPlaylist.id
      }, 100)
      
    },

    detailDone () {
      const self = this
      self.detailDialog = false

      self.playlists[self.editedIndex].playlistBlobs = []
      self.editedPlaylist.playlistBlobs.forEach(b => {
        self.playlists[self.editedIndex].playlistBlobs.push(b)
      })
      self.resetEditedPlaylist()
    },

    detailCancel () {
      this.detailDialog = false
      this.resetEditedPlaylist()
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.name.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        item.description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
                      ? 'Validation Failed'
                      : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    kioskId () {
      const self = this
      self.playlists = []

      if (self.kioskId > 0 && !self.isFromDashboard) {
        self.getKioskResidents().then(data => {  
          self.residents = data.residents
          self.residentId = null
        })
      }
    },

    residentId () {
      if (this.residentId > 0) {
        this.loadPlaylists()
      } else this.playlists = []
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    },

    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to name input in the edit dialog
          self.$refs.name.focus()
        }, 300)
      }
    }
  }
}
</script>
